/**
 * QRBatchExport - Batch export QR codes for delegates
 * Features:
 * - Download individual QR codes
 * - Batch download as ZIP
 * - Batch download as printable PDF
 * - WhatsApp-ready QR images
 * - Filter by phone-only delegates
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  Download, FileImage, FileText, Archive, Smartphone, Phone,
  Loader2, CheckCircle, Users, QrCode, Printer, Share2,
  AlertCircle, Mail, Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRBatchExport = ({ eventId, eventName, isOpen, onClose }) => {
  const [selectedDelegates, setSelectedDelegates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportType, setExportType] = useState('zip'); // zip, pdf, whatsapp
  const [pdfOptions, setPdfOptions] = useState({
    pageSize: 'letter',
    qrsPerPage: 6
  });
  const [filterPhoneOnly, setFilterPhoneOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get auth token
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  const headers = {
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  };

  // Fetch delegates
  const { data: delegates = [], isLoading } = useQuery({
    queryKey: ['delegates-for-qr', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/delegates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch delegates');
      return res.json();
    },
    enabled: isOpen && !!eventId
  });

  // Filter delegates based on search and phone-only filter
  const filteredDelegates = delegates.filter(d => {
    const matchesSearch = searchTerm === '' || 
      (d.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.phone || '').includes(searchTerm);
    
    const matchesPhoneOnly = !filterPhoneOnly || (!d.email && d.phone);
    
    return matchesSearch && matchesPhoneOnly;
  });

  // Phone-only delegates count
  const phoneOnlyCount = delegates.filter(d => !d.email && d.phone).length;

  // Toggle all selection
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedDelegates(filteredDelegates.map(d => d.id));
    } else {
      setSelectedDelegates([]);
    }
  };

  // Toggle single delegate
  const handleToggleDelegate = (delegateId) => {
    setSelectedDelegates(prev => {
      if (prev.includes(delegateId)) {
        return prev.filter(id => id !== delegateId);
      } else {
        return [...prev, delegateId];
      }
    });
  };

  // Download single QR
  const downloadSingleQR = async (delegateId, delegateName) => {
    try {
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/delegates/${delegateId}/qr/download?format=png&size=400&include_name=true`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      
      if (!res.ok) throw new Error('Download failed');
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_${delegateName.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded QR for ${delegateName}`);
    } catch (error) {
      toast.error(`Failed to download QR: ${error.message}`);
    }
  };

  // Get WhatsApp-ready QR
  const getWhatsAppQR = async (delegateId, delegateName) => {
    try {
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/delegates/${delegateId}/qr/whatsapp`,
        { headers }
      );
      
      if (!res.ok) throw new Error('Failed to generate WhatsApp QR');
      
      const data = await res.json();
      
      if (data.success) {
        // Create download link
        const byteCharacters = atob(data.image_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        
        // Download
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Copy message text to clipboard
        if (data.message_text) {
          navigator.clipboard.writeText(data.message_text);
          toast.success(`Downloaded! Message copied to clipboard for ${delegateName}`);
        }
      }
    } catch (error) {
      toast.error(`Failed: ${error.message}`);
    }
  };

  // Batch export
  const handleBatchExport = async () => {
    if (selectedDelegates.length === 0) {
      toast.error('Please select at least one delegate');
      return;
    }

    setExporting(true);
    setExportProgress(10);

    try {
      let endpoint, body, filename;
      
      if (exportType === 'zip') {
        endpoint = `${API_URL}/api/events/${eventId}/qr/batch-download`;
        body = {
          delegate_ids: selectedDelegates.length === filteredDelegates.length ? null : selectedDelegates,
          include_names: true,
          format: 'png'
        };
      } else if (exportType === 'pdf') {
        endpoint = `${API_URL}/api/events/${eventId}/qr/batch-pdf`;
        body = {
          delegate_ids: selectedDelegates.length === filteredDelegates.length ? null : selectedDelegates,
          page_size: pdfOptions.pageSize,
          qrs_per_page: pdfOptions.qrsPerPage
        };
      } else if (exportType === 'whatsapp') {
        // Send badges directly via OpenWA — no download required.
        setExportProgress(30);
        const res = await fetch(`${API_URL}/api/events/${eventId}/qr/batch-whatsapp`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            delegate_ids: selectedDelegates.length === filteredDelegates.length ? null : selectedDelegates,
          }),
        });
        setExportProgress(80);
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          throw new Error(error.detail || 'WhatsApp bulk send failed');
        }
        const data = await res.json();
        setExportProgress(100);
        toast.success(
          `${data.sent_count}/${data.delegates_total} queued · ${data.skipped_count} opt-outs · ${data.failed_count} failed`
        );
        setExporting(false);
        return;
      }

      setExportProgress(30);

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      setExportProgress(70);

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Export failed');
      }

      const data = await res.json();
      setExportProgress(90);

      if (data.success) {
        // Decode base64 and download
        const base64Data = exportType === 'zip' ? data.zip_base64 : data.pdf_base64;
        const mimeType = exportType === 'zip' ? 'application/zip' : 'application/pdf';
        
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setExportProgress(100);
        
        const message = exportType === 'zip' 
          ? `Downloaded ${data.count} QR codes as ZIP`
          : `Downloaded PDF with ${data.delegate_count} badges (${data.page_count} pages)`;
        
        toast.success(message);
      }
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            QR Code Export
          </DialogTitle>
          <DialogDescription>
            Download QR codes for {eventName || 'event'} delegates
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="batch" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="batch" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Batch Export
            </TabsTrigger>
            <TabsTrigger value="individual" className="flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              Individual
            </TabsTrigger>
          </TabsList>

          {/* Batch Export Tab */}
          <TabsContent value="batch" className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Export Options */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${exportType === 'zip' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setExportType('zip')}
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <Archive className="h-8 w-8 text-blue-500 mb-2" />
                  <div className="font-medium">ZIP Archive</div>
                  <div className="text-xs text-slate-500">Individual PNG files</div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${exportType === 'pdf' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setExportType('pdf')}
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <FileText className="h-8 w-8 text-red-500 mb-2" />
                  <div className="font-medium">PDF Badges</div>
                  <div className="text-xs text-slate-500">Printable format</div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${exportType === 'whatsapp' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setExportType('whatsapp')}
                data-testid="qr-export-whatsapp-card"
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <Smartphone className="h-8 w-8 text-green-500 mb-2" />
                  <div className="font-medium">WhatsApp Bulk</div>
                  <div className="text-xs text-slate-500">Send via OpenWA</div>
                </CardContent>
              </Card>
            </div>

            {/* PDF Options */}
            {exportType === 'pdf' && (
              <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label>Page Size:</Label>
                  <Select 
                    value={pdfOptions.pageSize} 
                    onValueChange={(v) => setPdfOptions(p => ({ ...p, pageSize: v }))}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="letter">Letter</SelectItem>
                      <SelectItem value="a4">A4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label>QR per page:</Label>
                  <Select 
                    value={String(pdfOptions.qrsPerPage)} 
                    onValueChange={(v) => setPdfOptions(p => ({ ...p, qrsPerPage: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="8">8</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search delegates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="phoneOnly" 
                  checked={filterPhoneOnly}
                  onCheckedChange={setFilterPhoneOnly}
                />
                <Label htmlFor="phoneOnly" className="text-sm flex items-center gap-1 cursor-pointer">
                  <Phone className="h-4 w-4" />
                  Phone-only ({phoneOnlyCount})
                </Label>
              </div>
            </div>

            {/* Selection Header */}
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm font-medium">
                  Select All ({filteredDelegates.length} delegates)
                </span>
              </div>
              <Badge variant="outline">
                {selectedDelegates.length} selected
              </Badge>
            </div>

            {/* Delegate List */}
            <ScrollArea className="flex-1 border rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : filteredDelegates.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <Users className="h-12 w-12 mb-2 opacity-50" />
                  <p>No delegates found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredDelegates.map(delegate => (
                    <div 
                      key={delegate.id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleToggleDelegate(delegate.id)}
                    >
                      <Checkbox 
                        checked={selectedDelegates.includes(delegate.id)}
                        onCheckedChange={() => handleToggleDelegate(delegate.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {delegate.name || `${delegate.first_name || ''} ${delegate.last_name || ''}`.trim() || 'Unnamed'}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          {delegate.email ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {delegate.email}
                            </span>
                          ) : delegate.phone ? (
                            <span className="flex items-center gap-1 text-orange-600">
                              <Phone className="h-3 w-3" />
                              {delegate.phone}
                              <Badge variant="outline" className="text-[10px] px-1 py-0">No Email</Badge>
                            </span>
                          ) : (
                            <span className="text-slate-400">No contact info</span>
                          )}
                        </div>
                      </div>
                      {delegate.tier_name && (
                        <Badge variant="secondary" className="text-xs">
                          {delegate.tier_name}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Progress */}
            {exporting && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Exporting...</span>
                  <span>{exportProgress}%</span>
                </div>
                <Progress value={exportProgress} />
              </div>
            )}

            {/* Export Button */}
            <Button 
              onClick={handleBatchExport}
              disabled={selectedDelegates.length === 0 || exporting}
              className="w-full"
              size="lg"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export {selectedDelegates.length} QR Codes as {exportType.toUpperCase()}
                </>
              )}
            </Button>
          </TabsContent>

          {/* Individual Tab */}
          <TabsContent value="individual" className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search delegates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Delegate List with Actions */}
            <ScrollArea className="flex-1 border rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="divide-y">
                  {filteredDelegates.map(delegate => {
                    const name = delegate.name || `${delegate.first_name || ''} ${delegate.last_name || ''}`.trim() || 'Unnamed';
                    const isPhoneOnly = !delegate.email && delegate.phone;
                    
                    return (
                      <div 
                        key={delegate.id}
                        className="flex items-center gap-3 p-3 hover:bg-slate-50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center gap-2">
                            {name}
                            {isPhoneOnly && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 text-orange-600 border-orange-300">
                                <Phone className="h-3 w-3 mr-1" />
                                Phone Only
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {delegate.email || delegate.phone || 'No contact'}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadSingleQR(delegate.id, name)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => getWhatsAppQR(delegate.id, name)}
                          >
                            <Smartphone className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Phone-only Alert */}
            {phoneOnlyCount > 0 && (
              <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-orange-800">
                    {phoneOnlyCount} delegate{phoneOnlyCount > 1 ? 's' : ''} without email
                  </div>
                  <div className="text-orange-700">
                    These delegates need their QR codes shared manually via WhatsApp or printed.
                    Use the WhatsApp button to get a share-ready image.
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default QRBatchExport;
